import { FC, useMemo } from "react"
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"

import { LoadingSpinner } from "components/general/LoadingSpinner"
import { buildAlertColumns } from "components/risk/columns"
import { CHART_TOOLTIP_STYLE } from "components/risk/constants"
import { RiskLevelCard } from "components/risk/dashboard/RiskLevelCard"
import { buildCategoryData, buildRiskDistribution } from "components/risk/helpers"
import { StatCard } from "components/stats/Card"
import { DataTable } from "components/tables/DataTable"

import { RiskDashboardData } from "services/risk/types"

interface Props {
  data: RiskDashboardData | null
  loading: boolean
  navigate: (to: string) => void
}

export const DashboardContent: FC<Props> = ({ data, loading, navigate }) => {
  const alertColumns = useMemo(() => buildAlertColumns(navigate), [navigate])

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full py-20">
        <LoadingSpinner size={48} />
      </div>
    )
  }

  if (!data) {
    return <p className="text-gray-400 dark:text-gray-500">Failed to load dashboard data.</p>
  }

  const { overview, category_breakdown, recent_alerts, duplicate_summary } = data
  const totalRiskPlayers =
    overview.risk_levels.level_1 + overview.risk_levels.level_2 + overview.risk_levels.level_3 + overview.risk_levels.level_4

  const riskDistribution = buildRiskDistribution(overview.risk_levels)
  const categoryData = buildCategoryData(category_breakdown)

  const metrics = [
    { label: "Total Players", value: overview.total_players },
    { label: "Flagged Today", value: overview.flagged_today },
    { label: "Auto-Actions Today", value: overview.auto_actions_triggered_today },
    { label: "Pending Duplicates", value: duplicate_summary.pending_reviews },
    { label: "Confirmed Duplicates", value: duplicate_summary.confirmed_today },
    { label: "High Severity", value: duplicate_summary.high_severity_count }
  ]

  return (
    <>
      {/* Risk Level Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
        {([1, 2, 3, 4] as const).map((level) => (
          <RiskLevelCard key={level} level={level} count={overview.risk_levels[`level_${level}`]} total={totalRiskPlayers} />
        ))}
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 w-full">
        {metrics.map((m) => (
          <StatCard key={m.label} title={m.label} value={m.value} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Risk Level Distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={riskDistribution} cx="50%" cy="45%" outerRadius={80} dataKey="value">
                {riskDistribution.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              {/* On-slice labels overlap when a slice is 0 or the pie is small — a bottom
                  legend scales with any data distribution without colliding text. */}
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Category Average Scores</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={categoryData} cx="50%" cy="45%" outerRadius={80} dataKey="value">
                {categoryData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Alerts Table */}
      <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl p-6 w-full">
        <h3 className="text-lg font-semibold mb-4">Recent Auto-Actions</h3>
        {recent_alerts.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">No recent auto-actions.</p>
        ) : (
          <DataTable rows={recent_alerts} columns={alertColumns} rowKey={(a) => a.id} />
        )}
      </div>
    </>
  )
}
